import type {
  ViewpointFeedCategoryItem,
  ViewpointFeedCategoryListResult,
  ViewpointFeedEngagementParam,
  ViewpointFeedEngagementState,
  ViewpointFeedPost,
  ViewpointFeedSliceQuery,
  ViewpointFeedSliceResult,
  ViewpointPinnedListQuery,
  ViewpointPinnedListResult,
} from "./ViewpointFeedTypes";

const mockFeedCategories: ViewpointFeedCategoryItem[] = [
  { id: "cat-mock-a", name: "宏观" },
  { id: "cat-mock-b", name: "行业" },
];

const mockBasePosts: ViewpointFeedPost[] = [
  {
    id: "vp-mock-1",
    content: "市场情绪示例：短线注意节奏，仅供参考。",
    title: "",
    titleMode: false,
    imageUrls: [],
    categoryId: "cat-mock-a",
    categoryName: "宏观",
    importance: "normal",
    visibility: "all",
    publishedAt: "2026-05-12 10:00:00",
    publisherLabel: "演示账号",
    publisherNickname: "行情君",
    hidden: false,
    audienceTagIds: [],
    audienceTagMatchMode: "any",
    syncToWechat: false,
    likeCount: 8,
    collectCount: 2,
    likedByMe: false,
    collectedByMe: false,
    pinned: true,
  },
  {
    id: "vp-mock-2",
    content: "第二条观点 mock，用于点赞收藏联调。",
    title: "【示例】短文标题",
    titleMode: true,
    imageUrls: [`${process.env.PUBLIC_URL || ""}/logo.png`],
    categoryId: "cat-mock-b",
    categoryName: "行业",
    importance: "important",
    visibility: "all",
    publishedAt: "2026-05-11 15:30:00",
    publisherLabel: "王某某",
    publisherNickname: "投研手记",
    hidden: false,
    audienceTagIds: [],
    audienceTagMatchMode: "any",
    syncToWechat: false,
    likeCount: 24,
    collectCount: 7,
    likedByMe: false,
    collectedByMe: false,
  },
];

const mockLikedIds = new Set<string>();
const mockCollectedIds = new Set<string>();

function clonePost(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    imageUrls: [...(p.imageUrls ?? [])],
    audienceTagIds: [...(p.audienceTagIds ?? [])],
  };
}

function attachEngagement(row: ViewpointFeedPost): ViewpointFeedPost {
  const liked = mockLikedIds.has(row.id);
  const collected = mockCollectedIds.has(row.id);
  return {
    ...row,
    likeCount: row.likeCount + (liked ? 1 : 0),
    collectCount: row.collectCount + (collected ? 1 : 0),
    likedByMe: liked,
    collectedByMe: collected,
  };
}

/** 与线上切片一致：按 publishedAt desc, id desc */
function sortDesc(a: ViewpointFeedPost, b: ViewpointFeedPost): number {
  const ta = String(a.publishedAt);
  const tb = String(b.publishedAt);
  if (ta !== tb) return ta < tb ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

function isOlderThan(row: ViewpointFeedPost, cursorAt: string, cursorId: string): boolean {
  const ta = String(row.publishedAt);
  if (ta < cursorAt) return true;
  if (ta > cursorAt) return false;
  return row.id < cursorId;
}

function isNewerThan(row: ViewpointFeedPost, cursorAt: string, cursorId: string): boolean {
  const ta = String(row.publishedAt);
  if (ta > cursorAt) return true;
  if (ta < cursorAt) return false;
  return row.id > cursorId;
}

function filterByCategory(
  rows: ViewpointFeedPost[],
  categoryId: string | undefined
): ViewpointFeedPost[] {
  const cid = categoryId?.trim();
  if (!cid) return rows;
  return rows.filter((r) => r.categoryId === cid);
}

export async function mockListViewpointCategories(query: {
  spaceId: string;
  spaceProductId: string;
}): Promise<ViewpointFeedCategoryListResult> {
  if (!query.spaceId?.trim() || !query.spaceProductId?.trim()) {
    return { list: [] };
  }
  return { list: mockFeedCategories.map((c) => ({ ...c })) };
}

function mockPinnedRows(): ViewpointFeedPost[] {
  return mockBasePosts
    .filter((r) => r.pinned)
    .map(clonePost)
    .map((r) => ({ ...r, pinned: true }));
}

function mockFeedRows(categoryId?: string): ViewpointFeedPost[] {
  let list = mockBasePosts.filter((r) => !r.pinned).map(clonePost);
  list = filterByCategory(list, categoryId);
  return list.sort(sortDesc);
}

export async function mockGetPinnedViewpoints(
  query: ViewpointPinnedListQuery
): Promise<ViewpointPinnedListResult> {
  if (!query.spaceId?.trim() || !query.spaceProductId?.trim()) {
    return { list: [] };
  }
  let list = mockPinnedRows();
  list = filterByCategory(list, query.categoryId);
  return { list: list.map((r) => attachEngagement({ ...r, pinned: true })) };
}

export async function mockGetOldViewpoints(
  query: ViewpointFeedSliceQuery
): Promise<ViewpointFeedSliceResult> {
  if (!query.spaceId?.trim() || !query.spaceProductId?.trim()) {
    return { list: [], hasMore: false };
  }
  const limit = query.limit && query.limit > 0 ? query.limit : 8;
  let list = mockFeedRows(query.categoryId);
  const cAt = query.cursorPublishedAt != null ? String(query.cursorPublishedAt) : "";
  const cId = query.cursorId?.trim() ?? "";
  if (cAt && cId) {
    list = list.filter((r) => isOlderThan(r, cAt, cId));
  }
  const hasMore = list.length > limit;
  const page = list.slice(0, limit).map((r) => attachEngagement(r));
  return { list: page, hasMore };
}

export async function mockGetLatestViewpoints(
  query: ViewpointFeedSliceQuery
): Promise<ViewpointFeedSliceResult> {
  if (!query.spaceId?.trim() || !query.spaceProductId?.trim()) {
    return { list: [], hasMore: false };
  }
  const limit = query.limit && query.limit > 0 ? query.limit : 8;
  const cAt = query.cursorPublishedAt != null ? String(query.cursorPublishedAt) : "";
  const cId = query.cursorId?.trim() ?? "";
  if (!cAt || !cId) {
    return { list: [], hasMore: false };
  }
  let list = mockFeedRows(query.categoryId)
    .filter((r) => isNewerThan(r, cAt, cId));
  list = filterByCategory(list, query.categoryId);
  const hasMore = list.length > limit;
  const page = list.slice(0, limit).map((r) => attachEngagement(r));
  return { list: page, hasMore };
}

export async function mockToggleViewpointLike(
  param: ViewpointFeedEngagementParam
): Promise<ViewpointFeedEngagementState> {
  const id = param.viewpointId?.trim();
  if (!id) {
    return { liked: false, collected: false };
  }
  if (mockLikedIds.has(id)) {
    mockLikedIds.delete(id);
  } else {
    mockLikedIds.add(id);
  }
  return {
    liked: mockLikedIds.has(id),
    collected: mockCollectedIds.has(id),
  };
}

export async function mockToggleViewpointCollect(
  param: ViewpointFeedEngagementParam
): Promise<ViewpointFeedEngagementState> {
  const id = param.viewpointId?.trim();
  if (!id) {
    return { liked: false, collected: false };
  }
  if (mockCollectedIds.has(id)) {
    mockCollectedIds.delete(id);
  } else {
    mockCollectedIds.add(id);
  }
  return {
    liked: mockLikedIds.has(id),
    collected: mockCollectedIds.has(id),
  };
}
